// ViewChipRow — one row of mutually exclusive view chips over a list
// ("Open · In progress · Resolved · All", "Needs decision 7 · Acknowledged 2 ·
// Spray planned 3 · All 340"). Each chip is a 44px-tall toggle button carrying
// aria-pressed, inside a named role="group", so a screen reader announces the
// row by its name and each chip as pressed or not.
//
// Extracted from Farm Problems' status filter when Scouting needed the same
// row with a count on each chip (FM02 UF-07) — the alternative was a second
// copy of the same classes, which is how two chip rows drift apart. Both pages
// render this one.
//
// A count is whatever the caller hands over, already decided: a number, "—"
// when the read behind it failed (never a 0 — readState.ts), or "…" while it
// is in flight. This component never invents one.
import type { ReactNode } from 'react'
import clsx from 'clsx'

/** The pressed and unpressed chip faces — Farm Problems' pickers share them. */
export const CHIP_ON  = 'bg-lime-700 border-lime-500 text-white'
export const CHIP_OFF = 'surface hairline-strong text-fg-secondary'
// min-w-11: "All" was 40px wide — the one sub-44 target on Farm Problems.
const CHIP = 'tap-target-y min-w-11 text-xs rounded-full px-3 py-1 border pressable inline-flex items-center justify-center gap-1.5'

export interface ViewChipOption<K extends string> {
  key:    K
  label:  ReactNode
  /** Shown after the label. Omit for no count. */
  count?: string
}

export default function ViewChipRow<K extends string>({ label, options, value, onChange, className }: {
  /** The group's accessible name — what the row chooses between. */
  label:    string
  options:  ReadonlyArray<ViewChipOption<K>>
  value:    K
  onChange: (key: K) => void
  className?: string
}) {
  return (
    <div className={clsx('flex flex-wrap gap-1.5', className)} role="group" aria-label={label}>
      {options.map(o => {
        const on = value === o.key
        return (
          <button key={o.key} type="button" onClick={() => onChange(o.key)} aria-pressed={on}
            className={clsx(CHIP, on ? CHIP_ON : `${CHIP_OFF} hover:bg-raised`)}>
            <span>{o.label}</span>
            {o.count !== undefined && (
              <span className={clsx('tabular-nums font-semibold', on ? 'text-white' : 'text-gray-400')}>{o.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
